import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import { formatRole } from '../utils/roleLabel';
import './COEDashboard.css';

const COEDashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useContext(AuthContext);

  const [stats, setStats] = useState({
    totalPools: 0,
    activePools: 0,
    completedPools: 0,
    pendingReview: 0,
    finalizedPapers: 0,
    certifiedFaculty: 0
  });

  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all pool papers
      const { data: poolsData } = await API.get('/university-pool/pool-papers');
      const pools = poolsData.pool_papers || [];

      // Fetch pools ready for COE review
      const { data: reviewData } = await API.get('/university-pool-coe/pools-ready');
      const pendingPools = reviewData.pools || [];

      // Fetch certified faculty count
      let certifiedFaculty = 0;
      try {
        const { data: certifiedData } = await API.get('/faculty-certification/certified-faculty');
        certifiedFaculty = certifiedData.count || 0;
      } catch (err) {
        console.error('Failed to fetch certified faculty count');
      }

      // Calculate stats
      const statsData = {
        totalPools: pools.length,
        activePools: pools.filter(p => p.status === 'active').length,
        completedPools: pools.filter(p => p.status === 'completed').length,
        pendingReview: pendingPools.filter(p => p.status !== 'completed').length,
        finalizedPapers: pools.filter(p => p.status === 'completed').length,
        certifiedFaculty
      };

      setStats(statsData);

    } catch (err) {
      showToast('Failed to fetch dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // const getStatusBadge = (status) => {
  //   const statusMap = {
  //     'draft': { class: 'draft', text: 'Draft' },
  //     'active': { class: 'active', text: 'Active' },
  //     'locked': { class: 'locked', text: 'Locked' },
  //     'completed': { class: 'completed', text: 'Completed' }
  //   };

  //   const statusInfo = statusMap[status] || { class: 'draft', text: status };
  //   return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  // };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="coe-dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Controller of Examinations Dashboard</h1>
          <p>Welcome back, {user?.name} ({formatRole(user?.role)})</p>
          <p>Manage university-wide question pools and examinations</p>
          <div className="role-badge">
            <span className="badge coe-role">COE</span>
          </div>
        </div>
        <div className="header-actions"></div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Icon name="ChartBar" size={28} /></div>
          <div className="stat-content">
            <h3>{stats.totalPools}</h3>
            <p>Total Pool Papers</p>
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon"><Icon name="CheckCircle" size={28} /></div>
          <div className="stat-content">
            <h3>{stats.activePools}</h3>
            <p>Active Pools</p>
          </div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon"><Icon name="Clock" size={28} /></div>
          <div className="stat-content">
            <h3>{stats.pendingReview}</h3>
            <p>Pending Review</p>
          </div>
        </div>
        <div className="stat-card info">
          <div className="stat-icon"><Icon name="Target" size={28} /></div>
          <div className="stat-content">
            <h3>{stats.finalizedPapers}</h3>
            <p>Finalized Papers</p>
          </div>
        </div>
        <div className="stat-card purple">
          <div className="stat-icon"><Icon name="GraduationCap" size={28} /></div>
          <div className="stat-content">
            <h3>{stats.certifiedFaculty}</h3>
            <p>Certified Faculty</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="coe-actions-card">
        <div className="coe-actions-header">
          <h2>Quick Actions</h2>
        </div>

        <div className="coe-quick-actions">
          <div className="coe-action-card" onClick={() => navigate('/coe/templates?tab=create')}>
            <div className="coe-action-icon coe-action-icon--blue"><Icon name="Clipboard" size={22} /></div>
            <div className="coe-action-title">Create Template</div>
            <div className="coe-action-desc">Define structure, total marks, and question criteria for examination papers.</div>
          </div>

          <div className="coe-action-card" onClick={() => navigate('/coe/templates?tab=extract')}>
            <div className="coe-action-icon coe-action-icon--amber"><Icon name="FileText" size={22} /></div>
            <div className="coe-action-title">Extract Template</div>
            <div className="coe-action-desc">Upload an existing question paper PDF to automatically extract template configuration.</div>
          </div>

          <div className="coe-action-card" onClick={() => navigate('/coe/my-pool-papers')}>
            <div className="coe-action-icon coe-action-icon--green"><Icon name="Book" size={22} /></div>
            <div className="coe-action-title">My Pool Papers</div>
            <div className="coe-action-desc">View and manage the university pool papers you have created.</div>
          </div>

          <div className="coe-action-card" onClick={() => navigate('/university-pool/coe')}>
            <div className="coe-action-icon coe-action-icon--purple"><Icon name="Scale" size={22} /></div>
            <div className="coe-action-title">Review Papers</div>
            <div className="coe-action-desc">Review moderator-generated sets and finalize papers for examinations.</div>
            {stats.pendingReview > 0 && (
              <span className="coe-action-badge">{stats.pendingReview} pending</span>
            )}
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default COEDashboard;